import { useCallback, useEffect, useRef, useState } from "react"

interface Suggestion {
  text: string
  magicKey: string
  isCollection: boolean
}

interface ArcgisSuggestionsHook {
  suggestions: Suggestion[]
  isLoading: boolean
  error: string | null
  refresh: (payload: { text: string; maxSuggestions: number }) => void
}

/**
 * How long the box must be quiet before a suggestion lookup is posted to the
 * host.
 *
 * `refresh` is wired to the address field's `onChange`, so it used to post one
 * `getArcgisSuggestions` — and therefore one billed ArcGIS `suggest` call —
 * per KEYSTROKE: a 30-character address was 28 requests of which only the last
 * one's answer was ever rendered. 300 ms is the usual typeahead figure: below
 * a fast typist's inter-key gap, so a burst collapses to one call, and short
 * enough that the list still feels like it tracks the box.
 */
const SUGGEST_DEBOUNCE_MS = 300

const useArcgisSuggestions = (): ArcgisSuggestionsHook => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  /** The lookup scheduled but not yet posted, so the next keystroke can drop it. */
  const pending = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const handleMessage = useCallback((event: MessageEvent) => {
    const { type, suggestions, error } = event.data

    if (type === "arcgisSuggestions") {
      setSuggestions(suggestions)
      setIsLoading(false)

      setError(error)
    }
  }, [])

  useEffect(() => {
    window.addEventListener("message", handleMessage)
    return () => {
      window.removeEventListener("message", handleMessage)
    }
  }, [handleMessage])

  // A lookup scheduled by the last keystroke before the dialog closed would
  // otherwise post to the host after this hook is gone, and its answer would
  // land on a listener that no longer exists.
  useEffect(() => () => clearTimeout(pending.current), [])

  const refresh = useCallback(
    (payload: { text: string; maxSuggestions: number }) => {
      // Drop whatever the previous keystroke scheduled: only the newest text
      // is worth asking about, and the older answer would arrive to overwrite
      // the newer one if both went out.
      clearTimeout(pending.current)

      setIsLoading(true)
      setError(null)
      if (payload.text.length < 3) {
        setIsLoading(false)
        setSuggestions([])
        return
      }
      pending.current = setTimeout(() => {
        window.parent.postMessage(
          {
            type: "getArcgisSuggestions",
            payload,
          },
          "*",
        )
      }, SUGGEST_DEBOUNCE_MS)
    },
    [],
  )

  return {
    suggestions,
    isLoading,
    error,
    refresh,
  }
}

export default useArcgisSuggestions
